import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component, inject, Inject, OnInit, Optional,
  signal,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Client } from '../../models/client.model';
import { AdminService } from '../../admin/services/admin.service';
import { BookingComponent } from '../../main/booking/booking.component';
import { OrderComponent } from '../../main/order/order.component';
import { RoleService } from '../../role/role.service';
import { ClientsService } from '../../services/clients.service';
import { Location } from '@angular/common';
import { catchError, of } from 'rxjs';

@Component({
    selector: 'app-crm-client',
    templateUrl: './crm-client.component.html',
    styleUrls: ['./crm-client.component.scss'],
    standalone: false
})
export class CrmClientComponent implements OnInit {
  client = signal<Client>(null);

  company: any;

  company_logo: string;

  forbidden = signal(false);

  loading = signal(false);

  saving = signal(false);

  private readonly _clientService = inject(ClientsService);

  clientForm = this.fb.group({
    rut: this.fb.control(''),
    name: this.fb.control(''),
    email: this.fb.control(''),
    phone: this.fb.control(''),
    address: this.fb.control(''),
    city: this.fb.control(''),
    rating: this.fb.control(0, { nonNullable: true, validators: [Validators.min(0), Validators.max(5)] }),
    custom_fields: this.fb.group({}),
  });

  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) private data,
    @Optional() private matDialogRef: MatDialogRef<CrmClientComponent>,
    private location: Location,
    public adminService: AdminService,
    public roleService: RoleService,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private fb: FormBuilder,
  ) { }

  ngOnInit() {
    this.activatedRoute.params.forEach((params) => {
      if (params.id) {
        this.load(params.id);
      } else {
        this.load(this.data.id);
      }
    });
    this.company_logo = this.adminService.local$.getValue().preferences.logo || 'assets/images/logo-anfruns.png';
    this.company = this.adminService.local$.getValue();
  }

  load(id) {
    this.loading.set(true);
    this._clientService.get(id).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 403) {
          this.forbidden.set(true);
        } else {
          console.error(error);
        }
        this.loading.set(true);

        return of(null);
      }),
    ).subscribe((response) => { 
      if (!response) {
        return;
      }

      this.loading.set(false);

      this.clientForm.patchValue(response.body);
      this.client.set(response.body);
      this.cdr.detectChanges();
    })
  }

  goBack() {
    if (this.matDialogRef instanceof MatDialogRef) {
      this.matDialogRef.close();
    } else {
      this.location.back();
    }
  }

  openOrder(order) {
    if (order.correlative_id) {
      this.dialog.open(OrderComponent, { data: order.id, minWidth: '80%' });
    } else {
      this.dialog.open(BookingComponent, { data: { id: order.id }, minWidth: '80%' });
    }
  }

  createBooking(vehicle) {
    this.dialog.open(BookingComponent, {
      minWidth: '80%',
      data: {
        object: vehicle,
        client: this.client(),
      },
    });
  }

  saveClient() {
    this.saving.set(true);
    this._clientService.update(this.client().id, this.clientForm.value).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error(error);
        return of(null);
      }),
    ).subscribe((response) => {
      this.saving.set(false);
      if (!response) {
        return;
      }

      this.client.set(response.body);
    });
  }

  sortVehicles(vehicles) {
    if (!vehicles || vehicles.length === 0) {
      return [];
    }

    return vehicles?.sort((a, b) => {
      const endedAtA = a.orders?.at(0)?.ended_at;
      const endedAtB = b.orders?.at(0)?.ended_at;
      
      // First prioritize vehicles with unfinished orders
      if (!endedAtA && endedAtB) {
        return -1; // a comes first (has unfinished order)
      }

      if (endedAtA && !endedAtB) {
        return 1; // b comes first (has unfinished order)
      }

      // Handle null/undefined years by treating them as the oldest (lowest priority)
      const yearA = a.year ?? -Infinity;
      const yearB = b.year ?? -Infinity;
      
      // Sort by year in descending order (newer first)
      if (yearB !== yearA) {
        return yearB - yearA;
      }
      
      // If years are equal, sort by kms in ascending order (lower first)
      return (a.kms || 0) - (b.kms || 0);
    });
  }
}
